import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Subscription } from 'rxjs';

import { BackLinkInterface } from '~/checkout/interfaces/back-link.interface';
import { CheckoutItem } from '~/checkout/model/checkout-item.model';
import { CheckoutService } from '~/checkout/services/checkout/checkout.service';
import { formatIsoDuration } from '~/core/helpers/duration-formatter/duration-formatter.helper';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { MatchMediaService } from '~/core/services/match-media/match-media.service';
import { TitleService } from '~/core/services/title/title.service';
import { WindowService } from '~/core/services/window/window.service';
import { LocaleService } from '~/i18n/services/locale.service';
import { ParamethorService } from '~/paramethor/paramethor.service';
import { ParamethorNameEnum } from '~/paramethor/names.enum';
import {
  PromoCodeInterface,
  PromoCodeTestInterface,
} from './promo-code/payment-promo-code.interface';
import { VtmnButtonVariantEnum } from '~/component/buttons/vtmn-button/enum/vtmn-button-variant.enum';
import { PromoCodeService } from '~/payment/services/promocode/promocode.service';
import { LoaderService } from '~/core/services/loader/loader.service';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { appConfig } from '~/app.config';

@Component({
  selector: 'app-checkout-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class CheckoutPaymentComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();

  public readonly iconType = VtmnPlayIconTypeEnum;
  public readonly paramethorNameEnum = ParamethorNameEnum;
  public readonly vtmnButtonVariant = VtmnButtonVariantEnum;

  public promoCodeResponse: PromoCodeInterface;
  public promoCodeValue: string;
  public isMobileDevice: boolean;
  public items: CheckoutItem[];
  public promoCodeStatus: {};

  constructor(
    private readonly checkoutService: CheckoutService,
    public readonly elementRef: ElementRef,
    private readonly localizeRouter: LocalizeRouterService,
    private readonly paramethorService: ParamethorService,
    private readonly titleService: TitleService,
    private readonly localeService: LocaleService,
    private readonly matchMediaService: MatchMediaService,
    public readonly windowService: WindowService,
    private readonly promoCodeService: PromoCodeService,
    private readonly loaderService: LoaderService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngOnInit(): void {
    this.titleService.setPageTitle('seo.checkout.payment.title');

    this.subscriptions.add(
      this.checkoutService.getCartItems().subscribe((items) => {
        this.items = items.map(
          (checkoutItem) => new CheckoutItem(checkoutItem)
        );
      })
    );

    this.subscriptions.add(
      this.matchMediaService.isMobileDevice().subscribe((isMobileDevice) => {
        this.isMobileDevice = isMobileDevice;
      })
    );

    if (this.isPromoCodeEnabled) {
      const previousCode = JSON.parse(this.promoCodeService.loadPromoCode());

      if (previousCode?.code) {
        if (
          previousCode?.activityId &&
          previousCode?.activityId !== this.items[0]?.activity?.sku
        ) {
          this.promoCodeService.clearPromoCode();
          return;
        }

        const previousCodeFormatted: PromoCodeTestInterface = {
          promoCode: previousCode.code,
        };
        this.checkPromoCode(previousCodeFormatted);
      }
    }
  }

  public get isPromoCodeEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.PromoCode);
  }

  public checkPromoCode(value: PromoCodeTestInterface): void {
    this.loaderService.start();

    if (value?.promoCode !== null) {
      // Filter cart content sent to API
      const cartContentFiltered = this.items.map((item) => {
        return {
          identifier: item.timeslot.identifier,
          adults: item.adults,
          children: item.children,
          offer: item.defaultOffer,
        };
      });

      this.promoCodeService
        .getPromoCode(value.promoCode, cartContentFiltered)
        .subscribe({
          next: (response) => {
            if (!response.cart) {
              this.loaderService.stop();
              return;
            }

            this.promoCodeService.setPromoCodeValue(response);

            const newItems = response.cart.map(
              (checkoutItem) => new CheckoutItem(checkoutItem)
            );

            this.items = this.items
              .map((item, i) => ({
                ...item,
                ...newItems[i],
              }))
              .map((checkoutItem) => new CheckoutItem(checkoutItem));

            this.promoCodeStatus = { success: true };
            this.loaderService.stop();
          },
          error: (error) => {
            this.setErrorMessage(error);
            this.loaderService.stop();
          },
        });
    }

    if (!value?.promoCode) {
      this.promoCodeService.setPromoCodeValue(null);
      this.promoCodeResponse = null;
      this.clearPromoCodeError();
      this.loaderService.stop();
    }
  }

  public get backLink(): BackLinkInterface {
    return {
      path: this.localizeRouter
        .translateRoute('/checkout.billing-address')
        .toString(),
    };
  }

  public get isBms(): boolean {
    return this.items[0]?.activity?.isBms;
  }

  public get getFormattedRefundTime(): string {
    return formatIsoDuration(
      getRefundTimeLimit(this.items[0]?.activity, appConfig.refundLimit),
      this.localeService.dateLocale,
      true
    );
  }

  public get onepayEnabled(): boolean {
    return this.paramethorService.isTrue(ParamethorNameEnum.Onepay);
  }

  public clearPromoCode(): void {
    this.checkPromoCode(null);
    this.promoCodeService.clearInputValue();
  }

  public clearPromoCodeError(): void {
    this.promoCodeStatus = {};
  }

  private setErrorMessage(error): void {
    if (error.status === 404) {
      this.promoCodeStatus = { not_found: true };
    } else if (error.status === 400 && error.message.includes('this user')) {
      this.promoCodeStatus = { user: true };
    } else if (
      error.status === 400 &&
      error.message.includes('this organizer')
    ) {
      this.promoCodeStatus = { organizer: true };
    } else if (
      error.status === 400 &&
      error.message.includes('this activity')
    ) {
      this.promoCodeStatus = { activity: true };
    } else if (error.status === 400) {
      this.promoCodeStatus = { used: true };
    } else {
      this.promoCodeStatus = { unknown: true };
    }
  }
}
